import { useEffect, useState } from "react";
import { getAccessErrorMessage, isValidReason } from "../../utils/accessErrors";

const MAX_REASON = 200;

/**
 * 권한 변경 사유 입력 모달 (spec R-GOV-01, R-FE-06). 사유 없이 저장할 수 없다.
 * onConfirm(reason)이 실패하면 data.type별 안내 문구(잠금 409 포함)를 모달 안에 보여준다.
 *
 * @param {{ isOpen: boolean, title: string, summary?: React.ReactNode, children?: React.ReactNode,
 *           confirmLabel?: string, danger?: boolean, extraInvalid?: boolean,
 *           onConfirm: (reason: string) => Promise<void>, onClose: () => void }} props
 */
function ReasonModal({
  isOpen,
  title,
  summary,
  children,
  confirmLabel = "변경 저장",
  danger = false,
  extraInvalid = false,
  onConfirm,
  onClose,
}) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setReason("");
      setError("");
      setSubmitting(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const canSubmit = isValidReason(reason) && !extraInvalid && !submitting;

  const handleConfirm = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");
    try {
      await onConfirm(reason.trim());
    } catch (err) {
      setError(getAccessErrorMessage(err));
      setSubmitting(false);
    }
  };

  const close = () => {
    if (!submitting) onClose();
  };

  return (
    <div className="access-modal-overlay" onClick={close}>
      <div
        className="access-modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="access-modal-header">
          <span className="access-modal-title">{title}</span>
          <button type="button" className="access-modal-close" onClick={close} aria-label="닫기">
            ✕
          </button>
        </div>
        <div className="access-modal-body">
          {summary && <div className="access-modal-summary">{summary}</div>}
          {children}
          <label className="access-label" htmlFor="access-reason">
            변경 사유 <span className="access-required">*</span>
          </label>
          <textarea
            id="access-reason"
            className="form-control"
            rows={3}
            maxLength={MAX_REASON}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="변경 사유를 입력하세요 (필수, 1~200자). 개인정보는 적지 마세요."
          />
          <div className="access-counter">
            {reason.length}/{MAX_REASON}
          </div>
          {error && (
            <div className="access-error" role="alert">
              {error}
            </div>
          )}
        </div>
        <div className="access-modal-footer">
          <button type="button" className="btn btn-light btn-sm" onClick={close} disabled={submitting}>
            취소
          </button>
          <button
            type="button"
            className={`btn btn-sm ${danger ? "btn-danger" : "btn-primary"}`}
            onClick={handleConfirm}
            disabled={!canSubmit}
          >
            {submitting ? "저장 중..." : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ReasonModal;
